/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'react-resource-router';

import IconButton from '@atlaskit/button/icon/button';
import MenuIcon from '@atlaskit/icon/core/menu';
import ButtonItem from '@atlaskit/menu/button-item';
import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';

import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { CollectFeedbackV2 } from '../../CollectFeedback';
import { messages } from '../messages';
import { getPageLinks } from '../navigationItems';
import { AnchorLinkItem, PageLinkItem } from '../NavLinkItems';
import { useSendFeedback } from '../useSendFeedback';

const PAGES_HEADING_ID = 'nav-menu-pages-heading';
const ON_THIS_PAGE_HEADING_ID = 'nav-menu-on-this-page-heading';

const styles = cssMap({
	// `IconButton` strips a `css` prop passed directly to it, so the responsive
	// toggle lives on this wrapper. Visible by default, hidden above the width
	// where the bar fits the inline nav and the right-hand controls. `58.51rem`
	// rather than `58.5rem`: matching values would make `max-width` and
	// `min-width` both match at exactly 58.5rem and hide every control at once.
	// The cost is a sub-pixel band where both are visible. See `NavButtons`'
	// `hiddenNarrow` for how 936px was measured.
	wrapper: {
		display: 'inline-flex',
		// The trigger is a fixed-size `IconButton`, so it has nothing to give when
		// the bar runs out of room. Only the site switcher's label is meant to
		// absorb the squeeze, so opt out of shrinking here as `FeedbackControl`
		// does.
		flexShrink: 0,
		'@media (min-width: 58.51rem)': {
			display: 'none',
		},
	},
	// Feedback only belongs in here once the inline button is gone, which
	// `feedbackButton` does at `max-width: 30rem`. `30.01rem` is the complement,
	// on the same reasoning as `wrapper` above: matching values would hide both at
	// exactly 30rem and leave no way to send feedback at that width, at the cost
	// of a sub-pixel band where both are offered.
	feedbackItem: {
		'@media (min-width: 30.01rem)': {
			display: 'none',
		},
	},
});

type NavMenuProps = {
	testId?: string;
};

/**
 * The narrow-viewport hamburger: a single `IconButton` at the outer end of
 * the bar's right-hand cluster, past the avatar, whose popout carries both
 * kinds of navigation this bar has, each under its own labelled `Section` so
 * the page-versus-anchor distinction the wide bar draws with separate
 * clusters survives collapsing into one menu.
 *
 * The sections listed are the current page's own, so the group is headed "On
 * this page" and appears only on a route that has any, nothing on `/cli` or
 * `/benchmark`.
 * That is deliberately narrower than the wide bar, where the same list hangs
 * off the About link (`NavItemAnchorMenu`) and is reachable from everywhere:
 * collapsed into one menu, a list of another page's sections reads as though
 * it belonged to this one.
 *
 * Below 30rem it also carries "Give feedback", which is where the bar's inline
 * feedback button hides and would otherwise leave the action unreachable.
 */
export const NavMenu = ({ testId = 'nav-menu' }: NavMenuProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [{ route }] = useRouter();
	const [isOpen, setIsOpen] = useState(false);
	const { identity: user, isAuthed } = useAtlassianUserContext();
	const { isFeedbackOpen, openFeedback, closeFeedback } = useSendFeedback();
	const pageLinks = getPageLinks(isAuthed);
	// The current page's own sections, which is what lets the group be headed
	// "On this page" without lying: on a route that has none, there is no group.
	const anchorItems = pageLinks.find((item) => item.routeName === route?.name)?.anchors ?? [];

	// Closing on every route change keeps a popout opened just before a push
	// from being left open, stale, behind whatever page the visitor lands on.
	useEffect(() => {
		setIsOpen(false);
	}, [route?.name]);

	return (
		<span css={styles.wrapper} data-testid={testId}>
			<Popup
				// Right-aligned: the trigger is the
				// outermost control on the bar, so a `bottom-start` popout would
				// grow from its left edge towards the viewport's right edge and
				// have nowhere to go.
				placement="bottom-end"
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				shouldRenderToParent
				testId={`${testId}-popup`}
				content={() => (
					// Closing on any click inside — including a modified click, which
					// still opens the target in a new tab — means neither `PageLinkItem`
					// nor `AnchorLinkItem` needs a callback of its own.
					<MenuGroup minWidth={192} maxWidth={320} onClick={() => setIsOpen(false)}>
						<Section isList titleId={PAGES_HEADING_ID}>
							<HeadingItem id={PAGES_HEADING_ID}>
								<FormattedMessage {...messages.navPagesHeading} />
							</HeadingItem>
							{pageLinks.map((item) => (
								<PageLinkItem key={item.routeName} {...item} />
							))}
						</Section>
						{anchorItems.length > 0 && (
							<Section isList titleId={ON_THIS_PAGE_HEADING_ID} hasSeparator>
								<HeadingItem id={ON_THIS_PAGE_HEADING_ID}>
									<FormattedMessage {...messages.navOnThisPageHeading} />
								</HeadingItem>
								{anchorItems.map((item) => (
									<AnchorLinkItem key={item.token} {...item} />
								))}
							</Section>
						)}
						{/*
						 * Hidden by CSS rather than left out on a width check, so it needs
						 * no viewport measurement and appears exactly where the inline
						 * button goes. The whole section is wrapped, not just the item, so
						 * its separator goes with it instead of leaving a stray rule above
						 * 30rem.
						 */}
						<span css={styles.feedbackItem}>
							<Section hasSeparator>
								<ButtonItem onClick={openFeedback} testId={`${testId}-send-feedback`}>
									<FormattedMessage {...messages.giveFeedback} />
								</ButtonItem>
							</Section>
						</span>
					</MenuGroup>
				)}
				trigger={(triggerProps: TriggerProps) => (
					<IconButton
						{...triggerProps}
						icon={MenuIcon}
						label={formatMessage(messages.navMenuLabel)}
						appearance="subtle"
						onClick={() => setIsOpen((prev) => !prev)}
						testId={`${testId}-trigger`}
					/>
				)}
			/>
			{/*
			 * Outside the `Popup`: the click that opens the form also closes the
			 * popout, which would unmount the form with it.
			 */}
			<CollectFeedbackV2
				atlassianAccountId={user.accountId}
				name={user.name}
				isOpen={isFeedbackOpen}
				onClose={closeFeedback}
			/>
		</span>
	);
};
